import { aplicarConfig } from './remote-config.store';
import { gravarCache as gravarCachePadrao, lerCache as lerCachePadrao } from './remote-config.cache';
import { validarPayload } from './remote-config.schema';
import { IRemoteConfigPayload } from './remote-config.types';

const TIMEOUT_MS = 5000;
const TENTATIVAS = 2;

export interface IIdentidadeBuild {
  appId: string;
  envId: string;
  configUrl: string;
}

export interface IResultadoBoot {
  ok: boolean;
  payload: IRemoteConfigPayload | null;
}

/**
 * Dependências passadas por parâmetro em vez de importadas e espionadas no
 * teste: bindings de módulo ES são read-only, então `spyOn(modulo, 'export')`
 * falha no build do Angular.
 */
export interface IDependenciasBoot {
  lerCache: () => IRemoteConfigPayload | null;
  gravarCache: (payload: IRemoteConfigPayload) => void;
}

const PADRAO: IDependenciasBoot = { lerCache: lerCachePadrao, gravarCache: gravarCachePadrao };

const enderecoDe = ({ configUrl, appId, envId }: IIdentidadeBuild): string =>
  `${configUrl}/v1/${appId}/${envId}.json`;

/**
 * O payload declara de qual app e ambiente ele é, e essa declaração precisa ser
 * conferida — não basta ele passar no schema.
 *
 * Sem isto, um build de produção adota o payload de desenvolvimento que outro
 * build deixou em cache na mesma origem: o fetch de `production.json` acontece e
 * tem sucesso, mas o app já bootou com hosts de dev — e o cache continua batendo
 * na mesma chave no launch seguinte, então o ciclo se repete.
 *
 * Vale igualmente para a rede: uma resposta trocada por proxy ou cache
 * intermediário não pode ser aplicada só por ter vindo com status 200.
 */
function pertenceAoBuild(
  payload: IRemoteConfigPayload,
  { appId, envId }: IIdentidadeBuild
): boolean {
  return payload.app === appId && payload.env === envId;
}

/**
 * AbortController manual em vez de AbortSignal.timeout(): este último só existe
 * a partir do Safari 16, e o app roda em WKWebView de versões mais antigas — um
 * `undefined` aqui estouraria justamente no caminho de boot.
 */
async function comTimeout(endereco: string): Promise<Response> {
  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), TIMEOUT_MS);
  try {
    // `cache: 'no-cache'` é obrigatório: sem ele o max-age=300 faz o próprio
    // WebView servir do cache HTTP e a revalidação nem sai do aparelho.
    return await fetch(endereco, { cache: 'no-cache', signal: controle.signal });
  } finally {
    clearTimeout(relogio);
  }
}

/**
 * O JS nunca vê um 304: quando o servidor responde 304, o cache do WebView
 * atualiza a entrada e resolve a promise como 200 com o corpo em cache. Por
 * isso só existe caminho de sucesso com corpo.
 */
async function buscar(identidade: IIdentidadeBuild): Promise<IRemoteConfigPayload | null> {
  for (let tentativa = 0; tentativa < TENTATIVAS; tentativa++) {
    try {
      const resposta = await comTimeout(enderecoDe(identidade));
      if (!resposta.ok) continue;

      const conteudo: unknown = await resposta.json();
      if (validarPayload(conteudo) && pertenceAoBuild(conteudo, identidade)) return conteudo;
      // Payload inválido não melhora com retry — é config errada, não rede ruim.
      return null;
    } catch {
      // Rede ou timeout: tenta de novo.
    }
  }
  return null;
}

/**
 * Roda antes do bootstrapApplication, com `fetch` nativo em vez de HttpClient:
 * nenhum dos interceptors (Token, Date, SentryError) faz sentido numa chamada
 * pré-login, e o HttpClient só existe depois que a aplicação sobe.
 */
export async function carregarConfig(
  identidade: IIdentidadeBuild,
  deps: IDependenciasBoot = PADRAO
): Promise<IResultadoBoot> {
  const doCache = deps.lerCache();

  if (doCache && pertenceAoBuild(doCache, identidade)) {
    aplicarConfig(doCache);
    // Revalida sem bloquear e aplica o que voltar, além de gravar. Só gravar
    // deixava a memória um launch atrás do serviço: o payload novo entrava no
    // cache, mas quem respondia a `resolverHost()` seguia sendo o antigo por
    // toda a sessão — endpoint e localStorage corretos, app usando o valor
    // velho.
    //
    // `url()` é avaliado ao montar cada requisição, então todo consumidor HTTP
    // acompanha a troca. SignalR NÃO: o endereço é resolvido uma vez no
    // `withUrl()` e o `withAutomaticReconnect` redisca o mesmo host — só um
    // builder novo pega o endereço novo, e hoje quem faz isso é apenas o chat
    // (`onclose` -> `scheduleConnectionRetry`). Os hubs de pagamento e ocupação
    // ficam no host antigo até o próximo launch.
    //
    // Consequência que o desenho anterior não tinha: durante uma migração de
    // host a sessão pode ficar partida entre dois backends — um fluxo de várias
    // etapas iniciado antes da revalidação continua depois dela no endereço
    // novo. Ver §18 do spec de desenho.
    void buscar(identidade).then((novo) => {
      if (!novo) return;
      // Aplicar antes de gravar: com os dados do site bloqueados a gravação é
      // um no-op, e mesmo assim a sessão fica com a configuração nova.
      aplicarConfig(novo);
      deps.gravarCache(novo);
    });
    return { ok: true, payload: doCache };
  }

  const daRede = await buscar(identidade);
  if (!daRede) return { ok: false, payload: null };

  aplicarConfig(daRede);
  deps.gravarCache(daRede);
  return { ok: true, payload: daRede };
}
